// src/components/work_orders/EditWorkOrderForm.js
// Enhanced modal form component for editing existing work orders with advanced features and debugging
// --- UPDATE: edit work order type / domain / category, the linked FlowSense record
// (asset / sensor / well / road), location and parent inspection — only changed fields are sent.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
    ModalCloseButton, Button, VStack, FormControl, FormLabel, Input, Select,
    Textarea, useColorModeValue, useToast, Text, HStack, Icon, Badge, Box,
    Divider, Alert, AlertIcon, AlertDescription, Collapse, IconButton,
    Tooltip, ScaleFade, InputGroup, InputRightElement, FormHelperText,
    Tag, TagLabel, Grid, GridItem, Fade, Progress, Skeleton, Stack,
    // --- UPDATE
    SimpleGrid,
    // --- END UPDATE
} from '@chakra-ui/react';
import {
    MdEdit, MdTitle, MdDescription, MdCalendarToday, MdPriorityHigh, MdPerson,
    MdAssignment, MdWarning, MdInfo, MdCheckCircle, MdBugReport, MdClose,
    MdHistory, MdCompareArrows, MdLightbulb,
    // --- UPDATE: Added MdTimer for SLA deadline display ---
    MdTimer,
    // --- END UPDATE ---
    // --- UPDATE
    MdCategory, MdLink,
    // --- END UPDATE
} from 'react-icons/md';

import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';
// --- UPDATE
import {
    WORK_ORDER_DOMAINS, WORK_ORDER_TYPES, DOMAIN_KEYS, TYPE_KEYS, categoriesFor, typesFor,
    getDomain, getType, inferDomain, inferType, validateForCreate,
} from './workOrderCatalog';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
// --- END UPDATE

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
const DEBUG_CONFIG = {
    enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
    logLevel: 'all',
    logToConsole: true,
    logToUI: false,
    logNetwork: true,
    logPerformance: true,
    logValidation: true,
};

const debugLog = {
    info: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error' || DEBUG_CONFIG.logLevel === 'warn') return;
        console.log(`%c[EditWO:${context}] ℹ️ ${message}`, 'color: #3182ce', data || '');
    },
    warn: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error') return;
        console.warn(`%c[EditWO:${context}] ⚠️ ${message}`, 'color: #d69e2e', data || '');
    },
    error: (context, message, error = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        console.error(`%c[EditWO:${context}] ❌ ${message}`, 'color: #e53e3e', error || '');
        if (error?.stack) console.error(error.stack);
    },
    network: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logNetwork) return;
        console.log(`%c[EditWO:${context}] 🌐 ${message}`, 'color: #805ad5', data || '');
    },
    performance: (context, message, duration) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logPerformance) return;
        const color = duration < 100 ? '#38a169' : duration < 500 ? '#d69e2e' : '#e53e3e';
        console.log(`%c[EditWO:${context}] ⚡ ${message} (${duration}ms)`, `color: ${color}`);
    },
    validation: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logValidation) return;
        console.log(`%c[EditWO:${context}] ✅ ${message}`, 'color: #38b2ac', data || '');
    },
};

class PerformanceMonitor {
    constructor(operation) {
        this.operation = operation;
        this.startTime = performance.now();
    }
    end() {
        const duration = (performance.now() - this.startTime).toFixed(2);
        debugLog.performance(this.operation, 'Operation completed', duration);
        return { duration };
    }
}

// ============================================================================
// API CALLS
// ============================================================================
const API_BASE_URL = config.apiUrl;

const handleApiError = async (response, resourceName = 'resource') => {
    let errorMsg = `HTTP error with ${resourceName}! Status: ${response.status}`;
    try {
        const errData = await response.json();
        errorMsg = errData.message || (errData.errors ? errData.errors.map(er => er.msg).join(', ') : errorMsg);
    } catch (e) {
        debugLog.error('handleApiError', 'Failed to parse error response', e);
    }
    throw new Error(errorMsg);
};

const updateWorkOrderApi = async (id, updateData, token) => {
    const monitor = new PerformanceMonitor('updateWorkOrderApi');
    debugLog.network('updateWorkOrderApi', `Updating Work Order ${id}`, updateData);

    const response = await fetch(`${API_BASE_URL}/workorders/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
    });

    if (!response.ok) {
        debugLog.error('updateWorkOrderApi', `Update failed: ${response.status}`);
        await handleApiError(response, `updating work order ${id}`);
    }

    const result = await response.json();
    monitor.end();
    debugLog.info('updateWorkOrderApi', 'Work order updated successfully', result);
    return result;
};

// ============================================================================
// CONSTANTS
// ============================================================================
const ALLOWED_PRIORITY = ['low', 'medium', 'high', 'critical'];

const PRIORITY_CONFIG = {
    low: {
        color: 'gray',
        icon: MdInfo,
        description: 'Low priority',
    },
    medium: {
        color: 'blue',
        icon: MdPriorityHigh,
        description: 'Medium priority',
    },
    high: {
        color: 'orange',
        icon: MdWarning,
        description: 'High priority',
    },
    critical: {
        color: 'red',
        icon: MdWarning,
        description: 'Critical priority',
    },
};

// --- UPDATE: editable catalog / link fields
const EDITABLE_CATALOG_FIELDS = [
    'domain', 'work_order_type', 'category', 'asset_id', 'sensor_id', 'well_id', 'road_id',
    'location_description', 'latitude', 'longitude', 'parent_work_order_id',
];
const LABELS = {
    domain: 'domain', work_order_type: 'type', category: 'category', asset_id: 'asset', sensor_id: 'sensor',
    well_id: 'well', road_id: 'road', location_description: 'location', latitude: 'latitude', longitude: 'longitude',
    parent_work_order_id: 'parent work order',
};
const str = (v) => (v === null || v === undefined ? '' : String(v));
// --- END UPDATE

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function EditWorkOrderForm({ isOpen, onClose, onSuccess, initialData }) {
    // --------------------------------------------------------------------------
    // STATE
    // --------------------------------------------------------------------------
    const [formData, setFormData]               = useState({});
    const [originalData, setOriginalData]       = useState({});
    const [isSubmitting, setIsSubmitting]       = useState(false);
    const [showDebugPanel, setShowDebugPanel]   = useState(DEBUG_CONFIG.logToUI);
    const [showChanges, setShowChanges]         = useState(false);
    const [showTips, setShowTips]               = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [characterCounts, setCharacterCounts] = useState({ title: 0, description: 0 });
    const [hasChanges, setHasChanges]           = useState(false);
    const [isLoading, setIsLoading]             = useState(false);

    const toast = useToast();

    // --------------------------------------------------------------------------
    // LIFECYCLE
    // --------------------------------------------------------------------------
    useEffect(() => {
        debugLog.info('useEffect:mount', 'Component mounted');
        return () => debugLog.info('useEffect:unmount', 'Component unmounted');
    }, []);

    // Populate form when initialData changes
    useEffect(() => {
        if (initialData) {
            setIsLoading(true);

            const formattedData = {
                title:            initialData.title || '',
                description:      initialData.description || '',
                priority:         initialData.priority || 'medium',
                due_date:         initialData.due_date ? String(initialData.due_date instanceof Date ? initialData.due_date.toISOString() : initialData.due_date).split('T')[0] : '', // --- UPDATE: Date objects too
                id:               initialData.id,
                status:           initialData.status,
                assigned_user_id: initialData.assigned_user_id,
                // --- UPDATE: Pass SLA deadline through so it can be displayed read-only ---
                sla_resolution_deadline: initialData.sla_resolution_deadline,
                // --- END UPDATE ---
                // --- UPDATE: catalog fields (older rows get an inferred domain / type to start from)
                domain:               str(initialData.domain) || str(inferDomain(initialData)),
                work_order_type:      str(initialData.work_order_type) || str(inferType(initialData)),
                category:             str(initialData.category),
                asset_id:             str(initialData.asset_id),
                sensor_id:            str(initialData.sensor_id),
                well_id:              str(initialData.well_id),
                road_id:              str(initialData.road_id),
                location_description: str(initialData.location_description),
                latitude:             str(initialData.latitude),
                longitude:            str(initialData.longitude),
                parent_work_order_id: str(initialData.parent_work_order_id),
                // --- END UPDATE
            };

            setFormData(formattedData);
            setOriginalData(formattedData);

            debugLog.info('useEffect:populateForm', 'Form populated with initial data', {
                workOrderId: initialData.id,
                title:       initialData.title,
                // --- UPDATE: Log SLA deadline for debugging ---
                sla_resolution_deadline: initialData.sla_resolution_deadline,
                // --- END UPDATE ---
            });

            setTimeout(() => setIsLoading(false), 300);
        }
    }, [initialData]);

    // Character counts
    useEffect(() => {
        setCharacterCounts({
            title:       formData.title?.length || 0,
            description: formData.description?.length || 0,
        });
    }, [formData.title, formData.description]);

    // Detect changes
    useEffect(() => {
        if (!originalData.id) return;

        const changed =
            formData.title       !== originalData.title       ||
            formData.description !== originalData.description ||
            formData.priority    !== originalData.priority    ||
            formData.due_date    !== originalData.due_date    ||
            EDITABLE_CATALOG_FIELDS.some(f => formData[f] !== originalData[f]); // --- UPDATE

        setHasChanges(changed);
        if (changed) {
            debugLog.validation('useEffect:changes', 'Form has unsaved changes');
        }
    }, [formData, originalData]);

    // --------------------------------------------------------------------------
    // VALIDATION
    // --------------------------------------------------------------------------
    const validateField = useCallback((name, value) => {
        const errors = {};

        switch (name) {
            case 'title':
                if (!value || value.trim().length === 0) {
                    errors.title = 'Title is required';
                } else if (value.length < 3) {
                    errors.title = 'Title must be at least 3 characters';
                } else if (value.length > 200) {
                    errors.title = 'Title must not exceed 200 characters';
                }
                break;
            case 'description':
                if (value && value.length > 2000) {
                    errors.description = 'Description must not exceed 2000 characters';
                }
                break;
            case 'due_date':
                if (value) {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (selectedDate < today) {
                        errors.due_date = 'Due date cannot be in the past';
                    }
                }
                break;
            default:
                break;
        }

        if (Object.keys(errors).length > 0) {
            debugLog.validation('validateField', `Validation errors for ${name}`, errors);
        }

        return errors;
    }, []);

    // --------------------------------------------------------------------------
    // HANDLERS
    // --------------------------------------------------------------------------
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        debugLog.info('handleInputChange', `Field "${name}" changed`, {
            oldValue: formData[name],
            newValue: value,
        });

        // --- UPDATE: changing the domain clears a category / type that does not belong to it
        if (name === 'domain') {
            setFormData(prev => {
                const next = { ...prev, domain: value };
                if (next.category && !categoriesFor(value).some(c => c.label === next.category) && Object.values(WORK_ORDER_TYPES).every(t => t.backendCategory !== next.category)) next.category = '';
                if (next.work_order_type && value && !typesFor(value).includes(next.work_order_type)) next.work_order_type = '';
                return next;
            });
            return;
        }
        // --- END UPDATE
        setFormData(prev => ({ ...prev, [name]: value }));

        const fieldErrors = validateField(name, value);
        setValidationErrors(prev => ({
            ...prev,
            ...fieldErrors,
            [name]: fieldErrors[name] || undefined,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const monitor = new PerformanceMonitor('handleSubmit');

        if (!formData.id) {
            debugLog.error('handleSubmit', 'Work Order ID missing');
            toast({ title: "Error", description: "Work Order ID missing.", status: "error", duration: 5000, isClosable: true });
            return;
        }

        const titleErrors = validateField('title', formData.title);
        if (Object.keys(titleErrors).length > 0) {
            debugLog.warn('handleSubmit', 'Validation failed', titleErrors);
            setValidationErrors(titleErrors);
            toast({ title: "Validation Error", description: titleErrors.title, status: "error", duration: 5000, isClosable: true });
            return;
        }

        if (!hasChanges) {
            debugLog.warn('handleSubmit', 'No changes to save');
            toast({ title: "No Changes", description: "No modifications detected", status: "info", duration: 3000 });
            return;
        }

        debugLog.info('handleSubmit', 'Starting update process', {
            workOrderId: formData.id,
            changes:     getChangedFields(),
        });

        setIsSubmitting(true);
        const token = getAuthToken();

        if (!token) {
            debugLog.error('handleSubmit', 'Auth token missing');
            toast({ title: "Authentication Error", description: "Authentication token missing", status: "error", duration: 5000, isClosable: true });
            setIsSubmitting(false);
            return;
        }

        // --- UPDATE: backend rules for the catalog fields (e.g. sensor jobs need a sensor ID)
        const hardErrors = validateForCreate({ ...formData, domain: formData.domain || undefined, work_order_type: formData.work_order_type || undefined });
        if (hardErrors.length > 0) {
            toast({ title: 'Missing information', description: hardErrors.join(' '), status: 'error', duration: 6000, isClosable: true });
            setIsSubmitting(false);
            return;
        }
        // --- END UPDATE

        const payload = {
            title:       formData.title,
            description: formData.description || null,
            priority:    formData.priority,
            due_date:    formData.due_date || null,
        };
        // --- UPDATE: only changed catalog / link fields; cleared fields are sent as null
        EDITABLE_CATALOG_FIELDS.forEach((f) => {
            if (formData[f] === originalData[f]) return;
            const v = String(formData[f] ?? '').trim();
            payload[f] = v === '' ? null : ((f === 'latitude' || f === 'longitude') ? Number(v) : v);
        });
        // Older rows have no stored type / domain — save the (shown) inferred values with any edit
        if (!initialData?.domain && formData.domain && payload.domain === undefined) payload.domain = formData.domain;
        if (!initialData?.work_order_type && formData.work_order_type && payload.work_order_type === undefined) payload.work_order_type = formData.work_order_type;
        // --- END UPDATE

        debugLog.network('handleSubmit', 'Submitting Work Order Update', payload);

        try {
            const result = await updateWorkOrderApi(formData.id, payload, token);

            debugLog.info('handleSubmit', 'Work order updated successfully', result);

            toast({
                title:       "Work Order Updated",
                description: `Work order "${formData.title}" has been updated successfully`,
                status:      "success",
                duration:    4000,
                isClosable:  true,
            });

            // --- UPDATE: tell the other FlowSense pages (asset / road / well views refresh)
            publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, {
                action: 'updated', id: formData.id, asset_id: formData.asset_id || null, road_id: formData.road_id || null,
                sensor_id: formData.sensor_id || null, well_id: formData.well_id || null, domain: formData.domain || null,
            });
            // --- END UPDATE
            monitor.end();
            onSuccess();
            onClose();
        } catch (err) {
            debugLog.error('handleSubmit', 'Update failed', err);
            toast({ title: "Update Failed", description: err.message, status: "error", duration: 7000, isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getChangedFields = useCallback(() => {
        const changes = {};
        if (formData.title       !== originalData.title)       changes.title       = { old: originalData.title,       new: formData.title       };
        if (formData.description !== originalData.description) changes.description = { old: originalData.description, new: formData.description };
        if (formData.priority    !== originalData.priority)    changes.priority    = { old: originalData.priority,    new: formData.priority    };
        if (formData.due_date    !== originalData.due_date)    changes.due_date    = { old: originalData.due_date,    new: formData.due_date    };
        // --- UPDATE
        EDITABLE_CATALOG_FIELDS.forEach((f) => {
            if (formData[f] !== originalData[f]) changes[LABELS[f]] = { old: originalData[f], new: formData[f] };
        });
        // --- END UPDATE
        return changes;
    }, [formData, originalData]);

    // --------------------------------------------------------------------------
    // THEME COLORS — all hoisted unconditionally
    // --------------------------------------------------------------------------
    const formLabelColor   = useColorModeValue("gray.700",   "gray.200");
    const modalBg          = useColorModeValue("white",      "gray.800");
    const grayTextColor    = useColorModeValue("gray.500",   "gray.400");
    const borderColor      = useColorModeValue("gray.200",   "gray.600");
    const cardBg           = useColorModeValue("gray.50",    "gray.700");
    const debugPanelBg     = useColorModeValue("yellow.50",  "yellow.900");
    const debugBorderColor = useColorModeValue("yellow.200", "yellow.700");
    const changesBg        = useColorModeValue("blue.50",    "blue.900");
    const changesBorder    = useColorModeValue("blue.200",   "blue.700");

    const getPriorityColor = (priority) => PRIORITY_CONFIG[priority]?.color || 'gray';

    // --- UPDATE
    const catalogBg = useColorModeValue('teal.50', 'rgba(49, 151, 149, 0.12)');
    const domainCfg = getDomain(formData.domain);
    const typeCfg = getType(formData.work_order_type);
    const typeOptions = useMemo(() => (formData.domain ? typesFor(formData.domain) : TYPE_KEYS), [formData.domain]);
    const categoryOptions = useMemo(() => {
        const list = categoriesFor(formData.domain).map(c => c.label);
        if (formData.category && !list.includes(formData.category)) list.unshift(formData.category); // keep legacy values visible
        return list;
    }, [formData.domain, formData.category]);
    const modBadge = (f) => (formData[f] !== originalData[f] ? <Badge colorScheme="yellow" fontSize="2xs" ml={1}>Modified</Badge> : null);
    // --- END UPDATE

    const changedFields = getChangedFields();
    const changeCount   = Object.keys(changedFields).length;

    // --------------------------------------------------------------------------
    // RENDER
    // --------------------------------------------------------------------------
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="xl">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ScaleFade in={isOpen} initialScale={0.95}>
                <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg} borderRadius="xl" boxShadow="2xl">

                    {/* ====================== HEADER ====================== */}
                    <ModalHeader>
                        <VStack align="stretch" spacing={2}>
                            <HStack spacing={3}>
                                <Icon as={MdEdit} boxSize={6} color="yellow.500" />
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="lg" fontWeight="bold">Edit Work Order</Text>
                                    <Text fontSize="sm" fontWeight="normal" color={grayTextColor}>
                                        ID: {initialData?.id}
                                    </Text>
                                </VStack>
                            </HStack>

                            {hasChanges && (
                                <Alert status="warning" borderRadius="md" size="sm">
                                    <AlertIcon />
                                    <AlertDescription fontSize="xs">
                                        You have {changeCount} unsaved {changeCount === 1 ? 'change' : 'changes'}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </VStack>
                    </ModalHeader>

                    <ModalCloseButton />

                    {/* ====================== BODY ====================== */}
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">

                            {/* Quick Actions */}
                            <HStack justify="space-between">
                                <HStack spacing={2}>
                                    <Tooltip label="View changes">
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            leftIcon={<MdCompareArrows />}
                                            onClick={() => setShowChanges(!showChanges)}
                                            colorScheme={showChanges ? 'blue' : 'gray'}
                                            isDisabled={!hasChanges}
                                        >
                                            {showChanges ? 'Hide' : 'Show'} Changes
                                        </Button>
                                    </Tooltip>

                                    <Tooltip label="Show tips">
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            leftIcon={<MdLightbulb />}
                                            onClick={() => setShowTips(!showTips)}
                                            colorScheme={showTips ? 'yellow' : 'gray'}
                                        >
                                            Tips
                                        </Button>
                                    </Tooltip>
                                </HStack>

                                {DEBUG_CONFIG.enabled && (
                                    <Tooltip label="Toggle debug panel">
                                        <IconButton
                                            icon={<MdBugReport />}
                                            size="xs"
                                            variant={showDebugPanel ? 'solid' : 'outline'}
                                            colorScheme="yellow"
                                            onClick={() => setShowDebugPanel(!showDebugPanel)}
                                            aria-label="Debug"
                                        />
                                    </Tooltip>
                                )}
                            </HStack>

                            {/* Tips Panel */}
                            <Collapse in={showTips} animateOpacity>
                                <Alert status="info" borderRadius="md" variant="left-accent">
                                    <AlertIcon />
                                    <Box fontSize="sm">
                                        <Text fontWeight="bold" mb={1}>Editing Tips:</Text>
                                        <VStack align="start" spacing={1} fontSize="xs">
                                            <Text>• Only editable fields are shown</Text>
                                            <Text>• Status and assignment are changed via table actions</Text>
                                            <Text>• Changes are highlighted in the summary panel</Text>
                                            <Text>• SLA deadlines are system-enforced and cannot be edited</Text>
                                        </VStack>
                                    </Box>
                                </Alert>
                            </Collapse>

                            {/* Changes Summary */}
                            <Collapse in={showChanges && hasChanges} animateOpacity>
                                <Box p={3} bg={changesBg} borderRadius="md" borderWidth="1px" borderColor={changesBorder}>
                                    <HStack mb={2}>
                                        <Icon as={MdHistory} />
                                        <Text fontSize="sm" fontWeight="bold">
                                            Changed Fields ({changeCount})
                                        </Text>
                                    </HStack>
                                    <VStack align="stretch" spacing={2} fontSize="xs">
                                        {Object.entries(changedFields).map(([field, values]) => (
                                            <Box key={field}>
                                                <Text fontWeight="bold" textTransform="capitalize">
                                                    {field.replace('_', ' ')}:
                                                </Text>
                                                <HStack spacing={2} ml={2}>
                                                    <Badge colorScheme="red">Old: {values.old || 'Empty'}</Badge>
                                                    <Icon as={MdCompareArrows} boxSize={3} />
                                                    <Badge colorScheme="green">New: {values.new || 'Empty'}</Badge>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </VStack>
                                </Box>
                            </Collapse>

                            {/* ---- Loading skeleton or form fields ---- */}
                            {isLoading ? (
                                <Stack spacing={4}>
                                    <Skeleton height="60px"  borderRadius="md" />
                                    <Skeleton height="100px" borderRadius="md" />
                                    <Skeleton height="60px"  borderRadius="md" />
                                    <Skeleton height="60px"  borderRadius="md" />
                                </Stack>
                            ) : (
                                <>
                                    {/* ---- Title ---- */}
                                    <FormControl isRequired isInvalid={!!validationErrors.title}>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdTitle} />
                                                <Text>Title</Text>
                                                {formData.title !== originalData.title && (
                                                    <Badge colorScheme="yellow" fontSize="xs">Modified</Badge>
                                                )}
                                            </HStack>
                                        </FormLabel>
                                        <InputGroup>
                                            <Input
                                                name="title"
                                                value={formData.title || ''}
                                                onChange={handleInputChange}
                                                focusBorderColor="yellow.500"
                                            />
                                            <InputRightElement>
                                                <Text fontSize="xs" color={grayTextColor}>
                                                    {characterCounts.title}/200
                                                </Text>
                                            </InputRightElement>
                                        </InputGroup>
                                        {validationErrors.title && (
                                            <FormHelperText color="red.500">
                                                {validationErrors.title}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    {/* ---- Description ---- */}
                                    <FormControl isInvalid={!!validationErrors.description}>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdDescription} />
                                                <Text>Description</Text>
                                                {formData.description !== originalData.description && (
                                                    <Badge colorScheme="yellow" fontSize="xs">Modified</Badge>
                                                )}
                                            </HStack>
                                        </FormLabel>
                                        <Textarea
                                            name="description"
                                            value={formData.description || ''}
                                            onChange={handleInputChange}
                                            rows={4}
                                            resize="vertical"
                                            focusBorderColor="yellow.500"
                                        />
                                        <FormHelperText>
                                            {characterCounts.description}/2000 characters
                                        </FormHelperText>
                                    </FormControl>

                                    {/* ---- Priority ---- */}
                                    <FormControl>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdPriorityHigh} />
                                                <Text>Priority</Text>
                                                {formData.priority !== originalData.priority && (
                                                    <Badge colorScheme="yellow" fontSize="xs">Modified</Badge>
                                                )}
                                            </HStack>
                                        </FormLabel>
                                        <Select
                                            name="priority"
                                            value={formData.priority || 'medium'}
                                            onChange={handleInputChange}
                                            focusBorderColor="yellow.500"
                                        >
                                            {ALLOWED_PRIORITY.map(p => (
                                                <option key={p} value={p}>
                                                    {p.charAt(0).toUpperCase() + p.slice(1)}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            <Badge colorScheme={getPriorityColor(formData.priority)} mr={1}>
                                                {formData.priority?.toUpperCase()}
                                            </Badge>
                                            {PRIORITY_CONFIG[formData.priority]?.description}
                                        </FormHelperText>
                                    </FormControl>

                                    {/* ---- Due Date ---- */}
                                    <FormControl isInvalid={!!validationErrors.due_date}>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdCalendarToday} />
                                                <Text>Due Date</Text>
                                                {formData.due_date !== originalData.due_date && (
                                                    <Badge colorScheme="yellow" fontSize="xs">Modified</Badge>
                                                )}
                                            </HStack>
                                        </FormLabel>
                                        <Input
                                            type="date"
                                            name="due_date"
                                            value={formData.due_date || ''}
                                            onChange={handleInputChange}
                                            focusBorderColor="yellow.500"
                                        />
                                        {validationErrors.due_date && (
                                            <FormHelperText color="red.500">
                                                {validationErrors.due_date}
                                            </FormHelperText>
                                        )}
                                    </FormControl>

                                    {/* --- UPDATE: type / domain / linked FlowSense record */}
                                    <Box bg={catalogBg} borderRadius="md" p={3} role="group" aria-label="Work type and links">
                                        <HStack mb={2}><Icon as={MdCategory} color="teal.500" /><Text fontSize="sm" fontWeight="bold">Type & linked record</Text></HStack>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Domain{modBadge('domain')}</FormLabel>
                                                <Select name="domain" size="sm" value={formData.domain || ''} onChange={handleInputChange} placeholder="—" aria-label="Domain">
                                                    {DOMAIN_KEYS.map(k => <option key={k} value={k}>{WORK_ORDER_DOMAINS[k].label}</option>)}
                                                </Select>
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Work order type{modBadge('work_order_type')}</FormLabel>
                                                <Select name="work_order_type" size="sm" value={formData.work_order_type || ''} onChange={handleInputChange} placeholder="—" aria-label="Work order type">
                                                    {typeOptions.map(k => <option key={k} value={k}>{WORK_ORDER_TYPES[k].label}</option>)}
                                                </Select>
                                                {typeCfg && <FormHelperText fontSize="2xs">{typeCfg.explain}</FormHelperText>}
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Category{modBadge('category')}</FormLabel>
                                                <Select name="category" size="sm" value={formData.category || ''} onChange={handleInputChange} placeholder="—" aria-label="Category">
                                                    {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                                </Select>
                                            </FormControl>
                                            {domainCfg?.linkField && (
                                                <FormControl>
                                                    <FormLabel fontSize="xs" color={formLabelColor} mb={1}>
                                                        <HStack spacing={1}><Icon as={MdLink} /><Text>{domainCfg.linkLabel}</Text>{modBadge(domainCfg.linkField)}</HStack>
                                                    </FormLabel>
                                                    <Input name={domainCfg.linkField} size="sm" value={formData[domainCfg.linkField] || ''} onChange={handleInputChange} aria-label={domainCfg.linkLabel} />
                                                </FormControl>
                                            )}
                                            <FormControl gridColumn={{ md: 'span 2' }}>
                                                <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Location{modBadge('location_description')}</FormLabel>
                                                <Input name="location_description" size="sm" value={formData.location_description || ''} onChange={handleInputChange} placeholder="Address / landmark" aria-label="Location" />
                                            </FormControl>
                                            <Input name="latitude" size="sm" type="number" step="any" value={formData.latitude || ''} onChange={handleInputChange} placeholder="Latitude" aria-label="Latitude" />
                                            <Input name="longitude" size="sm" type="number" step="any" value={formData.longitude || ''} onChange={handleInputChange} placeholder="Longitude" aria-label="Longitude" />
                                            <FormControl gridColumn={{ md: 'span 2' }}>
                                                <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Parent work order / inspection{modBadge('parent_work_order_id')}</FormLabel>
                                                <Input name="parent_work_order_id" size="sm" value={formData.parent_work_order_id || ''} onChange={handleInputChange} placeholder="Leave empty if this job stands alone" aria-label="Parent work order ID" />
                                            </FormControl>
                                        </SimpleGrid>
                                    </Box>
                                    {/* --- END UPDATE */}

                                    <Divider />

                                    {/* ---- Read-Only Information Panel ---- */}
                                    <Box
                                        p={4}
                                        bg={cardBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <HStack mb={3}>
                                            <Icon as={MdInfo} color="blue.500" />
                                            <Text fontSize="sm" fontWeight="bold">Read-Only Information</Text>
                                        </HStack>

                                        <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                            {/* Status */}
                                            <GridItem>
                                                <Text fontSize="xs" color={grayTextColor}>Status</Text>
                                                <Badge colorScheme="blue" mt={1}>
                                                    {formData?.status?.toUpperCase() || 'N/A'}
                                                </Badge>
                                            </GridItem>

                                            {/* Assigned To */}
                                            <GridItem>
                                                <Text fontSize="xs" color={grayTextColor}>Assigned To</Text>
                                                <Text fontSize="sm" mt={1}>
                                                    {formData?.assigned_user_id || 'Unassigned'}
                                                </Text>
                                            </GridItem>

                                            {/* --- UPDATE: SLA deadline shown as a read-only enforced alert --- */}
                                            <GridItem colSpan={2} mt={2}>
                                                <Alert
                                                    status="error"
                                                    variant="left-accent"
                                                    borderRadius="md"
                                                    py={2}
                                                >
                                                    <AlertIcon as={MdTimer} />
                                                    <Box>
                                                        <Text fontSize="xs" fontWeight="bold">
                                                            Strict SLA Deadline:
                                                        </Text>
                                                        <Text fontSize="sm">
                                                            {formData.sla_resolution_deadline
                                                                ? new Date(formData.sla_resolution_deadline).toLocaleString()
                                                                : 'No SLA Tracking'}
                                                        </Text>
                                                    </Box>
                                                </Alert>
                                            </GridItem>
                                            {/* --- END UPDATE --- */}
                                        </Grid>

                                        <Text fontSize="xs" color={grayTextColor} mt={3}>
                                            <Icon as={MdInfo} display="inline" mr={1} />
                                            Status and Assignment are changed via table actions or dedicated routes.
                                            SLA deadlines are system-enforced and cannot be manually overridden.
                                        </Text>
                                    </Box>

                                    {/* ---- Debug Panel ---- */}
                                    {DEBUG_CONFIG.enabled && showDebugPanel && (
                                        <Collapse in={showDebugPanel} animateOpacity>
                                            <Box
                                                p={3}
                                                bg={debugPanelBg}
                                                borderRadius="md"
                                                borderWidth="1px"
                                                borderColor={debugBorderColor}
                                                fontSize="xs"
                                                fontFamily="mono"
                                            >
                                                <HStack justify="space-between" mb={2}>
                                                    <HStack>
                                                        <Icon as={MdBugReport} />
                                                        <Text fontWeight="bold">Debug Panel</Text>
                                                    </HStack>
                                                    <IconButton
                                                        icon={<MdClose />}
                                                        size="xs"
                                                        onClick={() => setShowDebugPanel(false)}
                                                        aria-label="Close debug"
                                                    />
                                                </HStack>
                                                <VStack align="stretch" spacing={1}>
                                                    <Text><strong>Work Order ID:</strong> {formData.id}</Text>
                                                    <Text><strong>Has Changes:</strong> {hasChanges ? 'Yes' : 'No'}</Text>
                                                    <Text><strong>Change Count:</strong> {changeCount}</Text>
                                                    <Text><strong>Title Length:</strong> {characterCounts.title}/200</Text>
                                                    <Text><strong>Description Length:</strong> {characterCounts.description}/2000</Text>
                                                    <Text><strong>Validation Errors:</strong> {Object.keys(validationErrors).length}</Text>
                                                    <Text><strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}</Text>
                                                    <Text><strong>Submitting:</strong> {isSubmitting ? 'Yes' : 'No'}</Text>
                                                    {/* --- UPDATE: SLA deadline in debug panel --- */}
                                                    <Text>
                                                        <strong>SLA Deadline:</strong>{' '}
                                                        {formData.sla_resolution_deadline
                                                            ? new Date(formData.sla_resolution_deadline).toLocaleString()
                                                            : 'None'}
                                                    </Text>
                                                    {/* --- END UPDATE --- */}
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    )}
                                </>
                            )}
                        </VStack>
                    </ModalBody>

                    {/* ====================== FOOTER ====================== */}
                    <ModalFooter>
                        <Button
                            type="submit"
                            colorScheme="yellow"
                            mr={3}
                            isLoading={isSubmitting}
                            loadingText="Saving..."
                            leftIcon={<MdEdit />}
                            isDisabled={!hasChanges || !!validationErrors.title || isLoading}
                        >
                            Save Changes
                        </Button>
                        <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </ScaleFade>
        </Modal>
    );
}

EditWorkOrderForm.propTypes = {
    isOpen:      PropTypes.bool.isRequired,
    onClose:     PropTypes.func.isRequired,
    onSuccess:   PropTypes.func.isRequired,
    initialData: PropTypes.object,
};

export default EditWorkOrderForm;